import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { mapSubmissionModuleState } from '@framework/common-web/frontoffice/module/fsm/mapSubmissionModuleState.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import {
  createColumnFilter,
  FilterInputType,
} from '@framework/ui/components/table/TableColumnFilter.tsx';
import { IconProgressCheck } from '@tabler/icons-react';
import { Tooltip } from 'antd';
import { type ColumnType } from 'antd/es/table';
import { type IModuleDelegation } from '../../../module/delegation/DelegationLogic.ts';
import { ModuleValidationStateIconBadge } from '../../../module/form/title/ModuleValidationStateIconBadge.tsx';
import { getModuleTitle } from '../../../module/getModuleTitle.ts';
import { ModuleActionsCell } from './ModuleActionsCell.tsx';
import { OmissisSubRowLabel } from './OmissisSubRowLabel.tsx';

export interface IModuleTableRow {
  key: string;
  module: RichiestaModuloDTO;
  code: string;
  title: string;
  delegate: string;
  /** Denominazione della fattispecie, dai dati aggiuntivi del riepilogo */
  usecase: string;
  isOmissis: boolean;
}

export function getModuleRow(
  module: RichiestaModuloDTO,
  isOmissis: boolean,
  moduleDelegation?: IModuleDelegation,
  usecase?: string,
): IModuleTableRow {
  return {
    key: isOmissis ? `${module.id}-omissis` : (module.id ?? ''),
    module,
    code: module.modulo?.modulo?.codice ?? '',
    title: getModuleTitle(module),
    delegate: moduleDelegation?.compilation?.userName ?? '',
    usecase: usecase ?? '',
    isOmissis,
  };
}

export const modulesColumnsBase = {
  code: {
    title: t('submission-summary.codice', 'Codice'),
    key: 'code',
    dataIndex: 'code',
    ...createColumnFilter<IModuleTableRow>(FilterInputType.Text),
    onFilter: (value, record) =>
      record.code.toLowerCase().includes(String(value).toLowerCase()),
    render: (code: string, record: IModuleTableRow) =>
      record.isOmissis ? null : code,
  },
  title: {
    title: t('submission-summary.titolo', 'Titolo'),
    key: 'title',
    dataIndex: 'title',
    ...createColumnFilter<IModuleTableRow>(FilterInputType.Text),
    onFilter: (value, record) =>
      record.title.toLowerCase().includes(String(value).toLowerCase()),
    render: (title: string, record: IModuleTableRow) =>
      record.isOmissis ? <OmissisSubRowLabel /> : title,
  },
  usecase: {
    title: t('submission-summary.fattispecie', 'Fattispecie'),
    key: 'usecase',
    dataIndex: 'usecase',
    ...createColumnFilter<IModuleTableRow>(FilterInputType.Text),
    onFilter: (value, record) =>
      record.usecase.toLowerCase().includes(String(value).toLowerCase()),
    render: (usecase: string, record: IModuleTableRow) =>
      record.isOmissis ? null : usecase,
  },
  delegate: {
    title: t('submission-summary.delegato', 'Delegato'),
    key: 'delegate',
    dataIndex: 'delegate',
    ...createColumnFilter<IModuleTableRow>(FilterInputType.Text),
    onFilter: (value, record) =>
      record.delegate.toLowerCase().includes(String(value).toLowerCase()),
    render: (delegate: string, record: IModuleTableRow) =>
      record.isOmissis ? null : delegate,
  },
} satisfies Record<string, ColumnType<IModuleTableRow>>;

/**
 * Restituisce tutte le colonne per la tabella moduli,
 * inclusa la colonna azioni.
 *
 * `showUsecase` aggiunge la colonna "Fattispecie" che etichetta i moduli
 * con la denominazione della fattispecie dai dati aggiuntivi del riepilogo.
 */
export function getModulesColumns(
  submissionId: string,
  submission?: RichiestaDTO,
  showUsecase = false,
): ColumnType<IModuleTableRow>[] {
  return [
    {
      title: (
        <Tooltip title={t('submission-summary.stato', 'Stato')}>
          <TbIcon
            icon={IconProgressCheck}
            aria-label={t('submission-summary.stato', 'Stato')}
          />
        </Tooltip>
      ),
      key: 'state',
      width: 36,
      align: 'center' as const,
      render: (_, record) =>
        record.isOmissis ? null : (
          <ModuleValidationStateIconBadge
            validationState={record.module.statoValidazione}
            type={mapSubmissionModuleState(record.module.statoCompilazione)}
          />
        ),
    },
    modulesColumnsBase.code,
    modulesColumnsBase.title,
    // La fattispecie arriva dai dati aggiuntivi, consumati solo dal
    // Backoffice: senza di essi la colonna sarebbe interamente vuota.
    ...(showUsecase ? [modulesColumnsBase.usecase] : []),
    modulesColumnsBase.delegate,
    {
      title: t('submission-summary.azioni', 'Azioni'),
      key: 'actions',
      width: 120,
      align: 'right' as const,
      render: (_, record) => (
        <ModuleActionsCell
          submissionId={submissionId}
          record={record}
          submission={submission}
        />
      ),
    },
  ];
}
